/**
 * proveService — la pagina «Prove» della console (atlante cap. 489): le prove di centralino e chatbot da far provare alle
 * aziende interessate, e i link che le fanno partire.
 *
 * Tutto passa dalle funzioni `consoleProv*` del server, solo per il super utente: le raccolte non si leggono né si scrivono
 * dal browser, così c'è un solo scrittore e una sola regola per dire se una prova è pronta.
 *
 * Logging: ogni chiamata registra in console nome, durata ed esito.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/** Perché una prova non parte o un'azione è rifiutata. Chiavi del server, tradotte nella pagina (`prove:motivi.*`). */
export type MotivoProve =
  | 'whatsapp_spento'
  | 'numero_non_collegato'
  | 'agente_spento'
  | 'missione_spenta'
  | 'prova_spenta'
  | 'missione_non_valida'
  | 'categoria_non_valida'
  | 'azienda_non_impostata'
  | 'prova_non_trovata'
  | 'campi_mancanti'
  | 'invito_non_trovato';

export interface AziendaDelleProve {
  tenant_id: string;
  nome: string;
  pronta: boolean;
  mancano: MotivoProve[];
  numero: string | null;
  categorie: string[];
}

export interface Prova {
  id: string;
  etichetta: string;
  descrizione: string;
  missione_id: string;
  categoria: string | null;
  messaggio: string;
  attiva: boolean;
  pronta: boolean;
  motivo: MotivoProve | null;
}

export type StatoInvito = 'attivo' | 'scaduto' | 'usi_esauriti';

export interface InvitoProva {
  codice: string;
  prova_id: string;
  nome: string | null;
  creato_il: string | null;
  scade_il: string | null;
  usi: number;
  /** Null per un link pubblico: nessun tetto. */
  usi_massimi: number | null;
  ultimo_uso_il: string | null;
  stato: StatoInvito;
  link: string | null;
}

export interface DatiProve {
  azienda: AziendaDelleProve | null;
  missioni: { id: string; titolo: string }[];
  prove: Prova[];
  inviti: InvitoProva[];
}

export interface ProvaDaSalvare {
  id?: string;
  etichetta: string;
  descrizione: string;
  missione_id: string;
  categoria: string | null;
  messaggio: string;
  attiva: boolean;
}

async function chiama<I, O>(nome: string, dati: I): Promise<O> {
  const inizio = performance.now();
  try {
    const r = await httpsCallable<I, O>(functions, nome)(dati);
    console.info(`[prove] ${nome} ok in ${Math.round(performance.now() - inizio)} ms`);
    return r.data;
  } catch (err) {
    console.error(`[prove] ${nome} fallita dopo ${Math.round(performance.now() - inizio)} ms`, err);
    throw err;
  }
}

export const leggiProve = () => chiama<Record<string, never>, DatiProve>('consoleProveLeggi', {});
export const salvaProva = (prova: ProvaDaSalvare) => chiama<ProvaDaSalvare, { id: string }>('consoleProvaSalva', prova);
export const creaInvito = (dati: { prova_id: string; nome: string; giorni: number; pubblico: boolean }) =>
  chiama<typeof dati, { codice: string; link: string; scade_il: string }>('consoleProvaInvita', dati);
export const revocaInvito = (codice: string) => chiama<{ codice: string }, { codice: string }>('consoleProvaInvitoRevoca', { codice });

/** Il motivo scritto dal server sull'errore, se c'è; altrimenti null, e la pagina mostra il messaggio dell'errore. */
export function motivoDellErrore(err: unknown): MotivoProve | null {
  const motivo = (err as { details?: { motivo?: unknown } })?.details?.motivo;
  return typeof motivo === 'string' ? (motivo as MotivoProve) : null;
}
